<template>
  <div class="atb-flex atb-flex-row atb-gap-4 atb-py-6 atb-px-4">
    <div class="atb-flex-shrink">
      <Sidebar
        is-header
        color="basic"
        class="atb-border atb-rounded-lg"
        sidebar-opened-class="atb-w-76"
        section-class="atb-divide-y"
        header-class="atb-pt-0 atb-pr-0 atb-pl-6 atb-text-base atb-w-full atb-normal-case atb-font-medium atb-text-black"
        body-class="atb-flex atb-flex-col atb-gap-4 atb-justify-center atb-items-center atb-p-2"
        :sidebar-style="{ minHeight: 'calc(100vh - (207px))' }"
        :toggle-open-style="{ transform: 'translateX(240px)' }"
      >
        <template #header>
          {{ $t('selectDataSource') }}
        </template>
        <template #body>
          <div class="atb-flex atb-flex-row atb-gap-1 atb-pt-4">
            <Button
              id="validateSaveButton"
              type="button"
              motif="tertiary"
              :title="$t('save')"
              :disabled="!isDataDisplayed"
              @click="saveHandler"
            >
              <template #icon>
                <span
                  class="material-icons"
                  style="font-size: 18px"
                  >save</span
                >
              </template>
              {{ $t('save') }}
            </Button>
            <Button
              id="validateResetButton"
              type="submit"
              motif="tertiary"
              :title="$t('reset')"
              :disabled="isResetDisabled"
              @click="resetButtonHandler"
            >
              <template v-slot:icon>
                <span
                  class="material-icons"
                  style="font-size: 18px"
                >
                  {{ $t('sync') }}
                </span>
              </template>
              {{ $t('reset') }}
            </Button>
            <Button
              id="validateRunButton"
              type="button"
              motif="primary"
              :title="$t('run')"
              :disabled="!isSelectionValid"
              @click="runButtonHandler"
            >
              <template #icon>
                <span
                  class="material-icons"
                  style="font-size: 12px"
                  >arrow_forward_ios</span
                >
              </template>
              {{ $t('run') }}
            </Button>
          </div>
          <div class="atb-w-full atb-px-2">
            <Dropdown
              id="validateDropdownList"
              v-model="selectedDataSourceName"
              :items="dataSourceList"
              :label="$t('dataSource')"
              filterable
              field-label-class="option-class"
              active-item-class="d-active"
              :get-option-label="(e: any) => e.name"
              :get-option-value="(e: any) => e.name"
              @update:modelValue="dataSourceSelectedHandler"
              title="selectedDataSourceName"
            />
          </div>
          <div
            class="atb-w-full atb-px-2"
            v-if="isDataSourceSelected"
          >
            <span class="atb-font-medium"
              >{{ DomPurify.sanitize(selectedDataSourceName) }}:</span
            >
            <div
              v-for="(slot, index) in dataFileSlots"
              :key="slot.id"
              :id="'validateDataFileSlot-' + index"
              class="validate-data-file-slot"
            >
              <FormField
                :label="dataFileSlotLabel(slot, index)"
                required="true"
                class="atb-py-2"
              >
                <div class="atb-flex atb-flex-row atb-items-center atb-gap-1">
                  <Textbox
                    :id="
                      index === 0
                        ? 'validateChooseFile'
                        : 'validateChooseFile-' + index
                    "
                    v-model="slot.value"
                    input-type="file"
                    input-class="atb-text-sm"
                    accept=".txt,.xml,.dat,text/plain,text/xml"
                    @change="onDataFileChange($event, index)"
                  />
                  <button
                    v-if="isManualSlotMode && index > 0"
                    :id="'validateRemoveDataFile-' + index"
                    type="button"
                    class="material-icons atb-cursor-pointer atb-text-gray-600"
                    style="font-size: 18px"
                    :title="$t('removeDataFile')"
                    @click="removeDataFileSlot(index)"
                  >
                    close
                  </button>
                </div>
              </FormField>
            </div>
            <Button
              v-if="isManualSlotMode"
              id="validateAddDataFileButton"
              type="button"
              motif="tertiary"
              :title="$t('addDataFile')"
              @click="addDataFileSlot"
            >
              <template #icon>
                <span
                  class="material-icons"
                  style="font-size: 18px"
                  >add</span
                >
              </template>
              {{ $t('addDataFile') }}
            </Button>
          </div>
          <div
            id="validateFilterArgument"
            class="atb-w-full atb-px-2"
            v-if="isFilterArgumentDisplayed"
          >
            <span class="atb-font-medium">{{ $t('filterArguments') }}</span>
            <ul>
              <li
                v-for="(argument, index) in argumentValue"
                :key="index"
                id="validateArgumentList"
              >
                <FormField
                  :label="$t('argument') + argument + ':'"
                  required="true"
                  class="atb-py-1"
                  form-field-class="atb-flex atb-flex-row items-center atb-gap-4"
                >
                  <Textbox
                    v-model="argumentInput[index]"
                    :key="index"
                    value="argument"
                    id="validateArgumentInput"
                    input-type="text"
                    input-class="atb-text-sm"
                    field-control-class="atb-w-7/12 items-center"
                    @change="updateArguments()"
                  />
                </FormField>
              </li>
            </ul>
          </div>
        </template>
      </Sidebar>
    </div>
    <div class="atb-flex atb-flex-col atb-gap-4 atb-w-full">
      <div
        v-if="isAttributeDataDisplayed"
        class="eds-table-wrap atb-w-full atb-h-auto atb-flex atb-flex-col atb-gap-4"
      >
        <div class="atb-bg-white atb-rounded atb-p-3 atb-overflow-visible">
          <div class="atb-flex atb-justify-end atb-mb-2">
            <div class="atb-relative atb-inline-block">
              <button
                id="validateAttributeColumnToggleButton"
                ref="attributeColBtnRef"
                type="button"
                :title="$t('columnVisibility')"
                class="atb-inline-flex atb-items-center atb-gap-1.5 atb-px-3 atb-py-1.5 atb-text-sm atb-font-semibold atb-text-white atb-bg-blue-600 atb-border atb-border-blue-600 atb-rounded-md atb-shadow-sm atb-cursor-pointer hover:atb-bg-blue-700 hover:atb-border-blue-700 active:atb-bg-blue-800 atb-transition-colors atb-select-none"
                @click="toggleAttributeColumnPanel"
              >
                <span
                  class="material-icons atb-leading-none"
                  style="font-size: 18px"
                  >view_column</span
                >
                {{ $t('columns') }}
              </button>
              <Teleport to="body">
                <div
                  v-if="showAttributeColumnPanel"
                  :style="attributePanelStyle"
                  class="atb-fixed atb-z-[9999] atb-bg-white atb-border atb-border-gray-200 atb-rounded-lg atb-shadow-lg atb-p-3 atb-min-w-48 atb-max-h-64 atb-overflow-y-auto"
                >
                  <div
                    class="atb-font-semibold atb-mb-2 atb-text-sm atb-text-gray-700"
                  >
                    {{ $t('columnVisibility') }}
                  </div>
                  <div
                    v-for="col in attributeColumnDefs"
                    :key="col.field"
                    class="atb-py-1"
                  >
                    <Checkbox
                      :checked="attributeColumnVisibility[col.field]"
                      :label="col.headerName"
                      checkbox-label-class="atb-text-sm"
                      @update:checked="
                        toggleAttributeColumnVisibility(col.field, $event)
                      "
                    />
                  </div>
                </div>
              </Teleport>
            </div>
          </div>
          <AgGridVue
            id="validateAttributeGrid"
            class="ag-theme-eds atb-w-full atb-border-t atb-border-gray-300"
            :style="{ height: '100px' }"
            :column-defs="visibleAttributeColumnDefs"
            :default-col-def="defaultColDef"
            :row-data="attributeRowData"
            :pagination="false"
            @grid-ready="onGridReady"
          />
        </div>
      </div>

      <div
        v-if="isDataDisplayed"
        class="eds-table-wrap atb-w-full atb-h-auto atb-flex atb-flex-col atb-gap-4"
      >
        <div class="atb-flex-grow atb-bg-white atb-rounded atb-p-3">
          <div class="atb-flex atb-justify-end atb-mb-2">
            <div class="atb-relative atb-inline-block">
              <button
                id="validateColumnToggleButton"
                ref="filterColBtnRef"
                type="button"
                :title="$t('columnVisibility')"
                class="atb-inline-flex atb-items-center atb-gap-1.5 atb-px-3 atb-py-1.5 atb-text-sm atb-font-semibold atb-text-white atb-bg-blue-600 atb-border atb-border-blue-600 atb-rounded-md atb-shadow-sm atb-cursor-pointer hover:atb-bg-blue-700 hover:atb-border-blue-700 active:atb-bg-blue-800 atb-transition-colors atb-select-none"
                @click="toggleFilterColumnPanel"
              >
                <span
                  class="material-icons atb-leading-none"
                  style="font-size: 18px"
                  >view_column</span
                >
                {{ $t('columns') }}
              </button>
              <Teleport to="body">
                <div
                  v-if="showColumnPanel"
                  :style="filterPanelStyle"
                  class="atb-bg-white atb-border atb-border-gray-200 atb-rounded-lg atb-shadow-lg atb-p-3 atb-max-h-64 atb-overflow-y-auto"
                >
                  <div
                    class="atb-font-semibold atb-mb-2 atb-text-sm atb-text-gray-700"
                  >
                    {{ $t('columnVisibility') }}
                  </div>
                  <div
                    v-for="col in filterColumnDefs"
                    :key="col.field"
                    class="atb-py-1"
                  >
                    <Checkbox
                      :checked="columnVisibility[col.field]"
                      :label="col.headerName"
                      checkbox-label-class="atb-text-sm"
                      @update:checked="
                        toggleColumnVisibility(col.field, $event)
                      "
                    />
                  </div>
                </div>
              </Teleport>
            </div>
          </div>
          <AgGridVue
            id="validateFilterGrid"
            class="ag-theme-eds atb-w-full atb-border-t atb-border-gray-300"
            :style="{ height: 'calc(100vh - 350px)' }"
            :column-defs="visibleFilterColumnDefs"
            :default-col-def="defaultColDef"
            :row-data="filterRowData"
            :suppress-excel-export="true"
            :pagination="false"
            @grid-ready="onGridReady"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, ref, Ref, toRefs } from 'vue';
  import Sidebar from '@atb-ui/eds-vue/src/components/Sidebar.vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Textbox from '@atb-ui/eds-vue/src/components/Textbox.vue';
  import FormField from '@atb-ui/eds-vue/src/components/Form/FormField.vue';
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import Dropdown from '@atb-ui/eds-vue/src/components/Dropdown.vue';
  import {
    GridReadyEvent,
    ColumnApi,
    CsvExportParams,
    GridApi,
  } from 'ag-grid-community';
  import { validateGreaterThan } from '@/app/shared/utils/validation.util';
  import { useI18n } from 'vue-i18n';
  import dayjs from 'dayjs';
  import { PrependCell } from '@/app/shared/types/prepend-cell.interface';
  import { ValidateFilterCodeEvent } from '@/app/shared/types/events/validate-tab-event.interface';
  import { CodeType } from '@/app/shared/enums/code-type.enum';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import Checkbox from '@atb-ui/eds-vue/src/components/Checkbox.vue';
  import { Arfs } from '@/app/features/ams/shared/types/response/ValidateCode';
  import { Datasource } from '@/app/shared/types/datasource.interface';
  import { FileUploadEvent } from '@/app/shared/types/events/filters-event.interface';
  import DomPurify from 'dompurify';
  import { getFileExtension } from '@/app/shared/utils/file-extension.util';
  import validateDatasourceApi from '@/app/shared/services/validate-datasource-service';
  import { ValidateDatasourceInfo } from '@/app/shared/types/validate-datasource-info.interface';

  /**
   * One slot per data file to upload. Files are posted as repeated `files`
   * multipart parts in slot order, because AMS pairs them to sub-datasources
   * by index (bureau first, then client-data XML, or a multi's sub-datasources
   * sorted by name). `value` mirrors the file input so it can be cleared.
   */
  interface DataFileSlot {
    id: number;
    value: string;
    file: File | null;
    /** Sub-datasource this slot's file is for, when AMS told us the list. */
    subDatasourceName: string | null;
  }

  let nextSlotId = 0;
  const newDataFileSlot = (
    subDatasourceName: string | null = null
  ): DataFileSlot => ({
    id: nextSlotId++,
    value: '',
    file: null,
    subDatasourceName,
  });

  const dataFileSlots: Ref<Array<DataFileSlot>> = ref([newDataFileSlot()]);
  /**
   * Sub-datasource names from AMS for the selected datasource, in upload
   * order (e.g. ['ExpConsUSV7', 'Custom_Cherry_xml']). Empty when the lookup
   * failed, in which case the user adds slots manually.
   */
  const subDatasourceNames: Ref<Array<string>> = ref([]);
  const selectedDataSourceName: Ref<string> = ref('');
  const isDataSourceSelected: Ref<boolean> = ref(false);
  const isFilterArgument: Ref<boolean> = ref(false);
  const clientStore = useClientStore();
  const alertStore = useAlertStore();
  const client = clientStore.getSelectedClient;
  const dataSourceList: Datasource[] = client.dataSources;
  const ONE_MEGABYTES = 1048576;
  const { t } = useI18n();

  interface ValidateTabProps {
    codeType: CodeType;
  }

  interface ValidateTabEvent {
    (_event: 'validateCode', _params: ValidateFilterCodeEvent): void;
    (_event: 'arguments', selectedDataSourceName: string): void;
  }

  const props = defineProps<ValidateTabProps>();
  const emit = defineEmits<ValidateTabEvent>();
  const { codeType } = toRefs(props);

  const arfs: Ref<Arfs> = ref({} as Arfs);
  const isDataDisplayed: Ref<boolean> = ref(false);
  const argumentValue: Ref<number> = ref(0);

  const filterColumnDefs: Ref<Array<any> | undefined> = ref([]);
  const filterRowData: Ref<Array<any> | undefined> = ref([]);
  const filterHeaders: Ref<Array<any> | undefined> = ref([]);
  const filterRawData: Ref<Array<any> | undefined> = ref([]);

  const attributeColumnDefs: Ref<Array<any> | undefined> = ref([]);
  const attributeRowData: Ref<Array<any> | undefined> = ref([]);
  const attributeHeaders: Ref<Array<any> | undefined> = ref([]);
  const attributeRawData: Ref<Array<any> | undefined> = ref([]);

  const argumentInput: Ref<Array<any>> = ref([]);

  const showColumnPanel: Ref<boolean> = ref(false);
  const columnVisibility: Ref<Record<string, boolean>> = ref({});
  const filterColBtnRef: Ref<HTMLElement | null> = ref(null);
  const filterPanelStyle: Ref<Record<string, string>> = ref({});

  const showAttributeColumnPanel: Ref<boolean> = ref(false);
  const attributeColumnVisibility: Ref<Record<string, boolean>> = ref({});
  const attributeColBtnRef: Ref<HTMLElement | null> = ref(null);
  const attributePanelStyle: Ref<Record<string, string>> = ref({});

  const toggleAttributeColumnPanel = () => {
    showAttributeColumnPanel.value = !showAttributeColumnPanel.value;
    if (showAttributeColumnPanel.value && attributeColBtnRef.value) {
      const rect = attributeColBtnRef.value.getBoundingClientRect();
      attributePanelStyle.value = {
        position: 'fixed',
        top: rect.bottom + 4 + 'px',
        left: rect.right + 'px',
        transform: 'translateX(-100%)',
        whiteSpace: 'nowrap',
        width: 'max-content',
      };
    }
  };

  const toggleFilterColumnPanel = () => {
    showColumnPanel.value = !showColumnPanel.value;
    if (showColumnPanel.value && filterColBtnRef.value) {
      const rect = filterColBtnRef.value.getBoundingClientRect();
      filterPanelStyle.value = {
        position: 'fixed',
        top: rect.bottom + 4 + 'px',
        left: rect.right + 'px',
        transform: 'translateX(-100%)',
        whiteSpace: 'nowrap',
        width: 'max-content',
      };
    }
  };
  const gridApi: Ref<GridApi | null> = ref(null);
  const columnApi: Ref<ColumnApi | null> = ref(null);

  const defaultColDef = ref({
    resizable: true,
    sortable: true,
    flex: 1,
    minWidth: 100,
  });

  const mapInputDataToColHeaders = (
    attrOrFiltersHeaders: any
  ): Array<any> | undefined => {
    return attrOrFiltersHeaders?.map((x: any) =>
      Object.assign({}, { field: x, headerName: x, headerTooltip: x })
    );
  };

  const mapRawDataToRowData = (
    attrOrFilterRawData: any,
    headers: any
  ): Array<any> => {
    return (
      attrOrFilterRawData?.map((elem: any) => {
        const obj = Object.assign({});
        elem.forEach((element: any, index: any) => {
          const key = headers?.[index];
          key && (obj[key] = element);
        });
        return obj;
      }) || []
    );
  };

  const transformData = (inputData: any, selectedCodeType: CodeType) => {
    if (selectedCodeType === CodeType.ATTRIBUTE) {
      attributeHeaders.value = inputData?.[0];
      attributeColumnDefs.value = mapInputDataToColHeaders(inputData?.[0]);
      console.log('attribute column: ', attributeColumnDefs.value);

      const visibility: Record<string, boolean> = {};
      attributeColumnDefs.value?.forEach((col: any) => {
        visibility[col.field] = true;
      });
      attributeColumnVisibility.value = visibility;
      showAttributeColumnPanel.value = false;

      attributeRawData.value = inputData?.filter(
        (_elem: any, index: number) => index !== 0
      );

      attributeRowData.value = mapRawDataToRowData(
        attributeRawData.value,
        inputData?.[0]
      );
    } else if (selectedCodeType === CodeType.FILTER) {
      filterHeaders.value = inputData?.[0];
      filterColumnDefs.value = mapInputDataToColHeaders(inputData?.[0]);

      const visibility: Record<string, boolean> = {};

      filterColumnDefs.value?.forEach((col: any) => {
        visibility[col.field] = true;
      });
      columnVisibility.value = visibility;
      showColumnPanel.value = false;

      filterRawData.value = inputData?.filter(
        (_elem: any, index: number) => index !== 0
      );

      filterRowData.value = mapRawDataToRowData(
        filterRawData.value,
        inputData?.[0]
      );
    }
  };

  const onGridReady = (params: GridReadyEvent) => {
    gridApi.value = params.api;
    columnApi.value = params.columnApi;
  };

  const resetDataFileSlots = (): void => {
    subDatasourceNames.value = [];
    dataFileSlots.value = [newDataFileSlot()];
  };

  /** True when AMS did not tell us the sub-datasources, so the user adds slots. */
  const isManualSlotMode = computed(
    () => subDatasourceNames.value.length === 0
  );

  /**
   * Asks AMS which sub-datasources the selected datasource validates against
   * and renders one labelled slot per name, in upload order. Falls back to a
   * single manual slot if the lookup fails, so Validate still works.
   */
  const loadSubDatasources = async (dataSourceName: string): Promise<void> => {
    try {
      const response = await validateDatasourceApi.getValidateDatasourceInfo(
        client.id,
        dataSourceName
      );
      const info: ValidateDatasourceInfo | undefined = response?.data?.data;
      const names = info?.subDatasourceNames ?? [];
      // Ignore a late response for a datasource that is no longer selected.
      if (selectedDataSourceName.value !== dataSourceName || !names.length) {
        return;
      }
      subDatasourceNames.value = names;
      dataFileSlots.value = names.map((name) => newDataFileSlot(name));
    } catch (error) {
      console.warn(
        'Could not resolve sub-datasources for Validate, falling back to manual data file slots: ',
        error
      );
    }
  };

  const dataSourceSelectedHandler = async () => {
    resetDataFileSlots();
    isDataSourceSelected.value = true;
    if (codeType.value?.toUpperCase() === CodeType.FILTER) {
      argumentHandler(selectedDataSourceName.value);
    }
    await loadSubDatasources(selectedDataSourceName.value);
  };

  const dataFileSlotLabel = (slot: DataFileSlot, index: number): string => {
    if (slot.subDatasourceName) {
      return t('dataFileFor', { name: slot.subDatasourceName });
    }
    return index === 0
      ? t('dataFile')
      : t('additionalDataFile', { index: index + 1 });
  };

  const resetButtonHandler = (): void => {
    selectedDataSourceName.value = '';
    isDataSourceSelected.value = false;
    isFilterArgument.value = false;
    resetDataFileSlots();
    isDataDisplayed.value = false;
    argumentInput.value.length = 0;
  };

  const addDataFileSlot = (): void => {
    dataFileSlots.value.push(newDataFileSlot());
  };

  const removeDataFileSlot = (index: number): void => {
    if (index > 0 && index < dataFileSlots.value.length) {
      dataFileSlots.value.splice(index, 1);
    }
  };

  const clearDataFileSlot = (slot: DataFileSlot): void => {
    slot.value = '';
    slot.file = null;
  };

  /**
   * Stores the chosen file in its slot only. Other slots are untouched, so
   * picking the second file never drops the first one.
   */
  const onDataFileChange = ($event: FileUploadEvent, index: number) => {
    const slot = dataFileSlots.value[index];
    if (!slot) {
      return;
    }
    const fileList: Array<File> | undefined = ($event as any)?.target?.files;
    const uploadFile = fileList?.[0];
    if (!uploadFile) {
      clearDataFileSlot(slot);
      return;
    }
    if (
      !verifyUploadFileSize(uploadFile) ||
      !verifyUploadFileType(uploadFile)
    ) {
      clearDataFileSlot(slot);
      return;
    }
    slot.file = uploadFile;
  };

  const verifyUploadFileSize = (uploadFile: File): boolean => {
    if (validateGreaterThan(uploadFile.size, ONE_MEGABYTES)) {
      alertStore.showNotification(
        t('fileUploadSizeError'),
        'File Upload',
        Motif.ERROR
      );
      return false;
    }
    return true;
  };

  const verifyUploadFileType = (uploadFile: File): boolean => {
    const fileExt = getFileExtension(uploadFile.name)?.toLowerCase();
    if (!(fileExt === 'txt' || fileExt === 'xml' || fileExt === 'dat')) {
      alertStore.showNotification(
        t('fileUploadExtensionError'),
        'File Upload',
        Motif.ERROR
      );
      return false;
    }
    return true;
  };

  const selectedFiles = computed((): Array<File> => {
    return dataFileSlots.value
      .map((slot) => slot.file)
      .filter((file): file is File => file !== null);
  });

  /**
   * Every slot must hold a file so the multipart index order matches the
   * AMS sub-datasource order (an empty slot would shift later files).
   */
  const isSelectionValid = computed(() => {
    return !!(
      selectedDataSourceName.value?.length &&
      dataFileSlots.value.length > 0 &&
      dataFileSlots.value.every((slot) => slot.file !== null)
    );
  });

  const isResetDisabled = computed(() => {
    return !(
      selectedDataSourceName.value?.length || selectedFiles.value.length
    );
  });

  const buildFormData = (): FormData => {
    const formData = new FormData();
    selectedFiles.value.forEach((file) => {
      formData.append('files', file, file.name);
    });
    return formData;
  };

  const isAttributeDataDisplayed = computed(() => {
    return (
      codeType.value?.toUpperCase() === CodeType.ATTRIBUTE &&
      isDataDisplayed.value
    );
  });

  const isFilterArgumentDisplayed = computed(() => {
    return (
      codeType.value?.toUpperCase() === CodeType.FILTER &&
      isFilterArgument.value
    );
  });

  const visibleFilterColumnDefs = computed(() => {
    return filterColumnDefs.value?.filter(
      (col: any) => columnVisibility.value[col.field] !== false
    );
  });

  const visibleAttributeColumnDefs = computed(() => {
    return attributeColumnDefs.value?.filter(
      (col: any) => attributeColumnVisibility.value[col.field] !== false
    );
  });

  const toggleColumnVisibility = (field: string, visible: boolean) => {
    columnVisibility.value = { ...columnVisibility.value, [field]: visible };
  };

  const toggleAttributeColumnVisibility = (field: string, visible: boolean) => {
    attributeColumnVisibility.value = {
      ...attributeColumnVisibility.value,
      [field]: visible,
    };
  };

  const updateArguments = () => {
    argumentInput.value.push();
  };

  const argumentHandler = (dataSourceName: string) => {
    emit('arguments', dataSourceName);
  };

  const toggleisFilterArgument = (show: boolean) => {
    isFilterArgument.value = show;
  };

  const toggleIsDataDisplayed = (show: boolean) => {
    isDataDisplayed.value = show;
  };

  const setArgumentValue = (value: number) => {
    argumentValue.value = value;
  };

  const setArfs = (response: Arfs) => {
    arfs.value = response;
  };

  const prependResultData = (data: string[] | undefined) => {
    return data?.map((elem: any) =>
      Object.assign({}, { data: { value: elem, type: 'String' } })
    );
  };

  const saveHandler = () => {
    const fileName = dayjs().format('MMDDYYHHmm');
    const prependData: PrependCell[][] = [];

    const headerData = prependResultData(arfs?.value?.header);
    headerData && prependData.push([...headerData]);

    const resultData = prependResultData(arfs?.value?.result);
    resultData && prependData.push([...resultData]);

    const subDatasourceNamesData = prependResultData(
      arfs?.value?.subDatasourceNames
    );
    subDatasourceNamesData && prependData.push([...subDatasourceNamesData]);

    let headerPrependData: any = undefined;
    let rowPrependData: any = undefined;
    if (codeType.value?.toUpperCase() === CodeType.FILTER) {
      headerPrependData = prependResultData(filterHeaders.value);
      rowPrependData = prependResultData(filterRawData.value);
    } else if (codeType.value?.toUpperCase() === CodeType.ATTRIBUTE) {
      headerPrependData = prependResultData(attributeHeaders.value);
      rowPrependData = prependResultData(attributeRawData.value);
    }
    headerPrependData && prependData.push([...headerPrependData]);
    rowPrependData && prependData.push([...rowPrependData]);

    const params: CsvExportParams = {
      fileName: fileName,
      prependContent: prependData,
    };
    gridApi.value?.exportDataAsCsv(params);
  };

  const runButtonHandler = async () => {
    emit('validateCode', {
      dataSourceName: selectedDataSourceName.value,
      file: buildFormData(),
      argumentsInput: argumentInput.value,
    } as ValidateFilterCodeEvent);
  };

  defineExpose({
    gridApi,
    transformData,
    toggleIsDataDisplayed,
    setArfs,
    toggleisFilterArgument,
    setArgumentValue,
    isDataSourceSelected,
  });
</script>
